import { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import { edgeService } from '../../services';
import { usePermissions } from '../../hooks/usePermissions';

interface ConfigState {
  edgeId: string;
  isDirty: boolean;
  dirtyReason: string | null;
  syncStatus: 'synced' | 'pending' | 'applying' | 'failed' | 'unknown';
  lastError: string | null;
  sequenceNumber: number;
  edgeAckedSeq: number;
  lastPushedAt: string | null;
  lastAppliedAt: string | null;
}

interface SyncConfigButtonProps {
  edgeId: string;
}

const SyncConfigButton = ({ edgeId }: SyncConfigButtonProps) => {
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [configState, setConfigState] = useState<ConfigState | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!edgeId) return;

    const fetchConfigState = async (isBackground = false) => {
      try {
        const options = isBackground ? { showLoading: false } : undefined;
        const state = await edgeService.getConfigState(edgeId, options);
        setConfigState(state);
      } catch (err) {
        console.error('Failed to fetch config state:', err);
      }
    };

    fetchConfigState();
    const interval = setInterval(() => fetchConfigState(true), 10000);
    return () => clearInterval(interval);
  }, [edgeId]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await edgeService.syncConfiguration(edgeId);

      if (result.status === 'warning') {
        toast.warning('Configuration Sync', result.message);
      } else {
        const commandCount = result.commandCount || 0;
        const topic = result.topic || '';
        toast.success(
          'Configuration Sync',
          `Configuration sent successfully. ${commandCount} commands published to topic: ${topic}`,
        );
      }
      setShowConfirm(false);
    } catch (err: any) {
      toast.error('Sync Failed', err.message || 'Failed to sync configuration');
      console.error('Sync config error:', err);
      setShowConfirm(false);
    } finally {
      setSyncing(false);
    }
  };

  if (!canUpdate) return null;

  return (
    <>
      <button
        onClick={() => setShowConfirm(true)}
        disabled={syncing}
        className={`inline-flex items-center space-x-2 disabled:opacity-50 px-4 py-2.5 rounded-md font-medium text-sm ${
          configState?.syncStatus === 'failed'
            ? 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-300'
            : configState?.isDirty
              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
        }`}
        title={
          configState?.isDirty
            ? `Changes pending: ${configState.dirtyReason}`
            : 'Force full configuration synchronization'
        }
      >
        {configState?.syncStatus === 'failed' ? (
          <AlertCircle className="w-4 h-4" />
        ) : configState?.isDirty ? (
          <AlertTriangle className="w-4 h-4" />
        ) : configState?.syncStatus === 'synced' ? (
          <CheckCircle className="w-4 h-4" />
        ) : (
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
        )}
        <span>Sync Config</span>
        {configState?.isDirty && (
          <span className="ml-1 px-1.5 py-0.5 bg-yellow-200 text-yellow-900 text-xs rounded-full">
            Pending
          </span>
        )}
        {configState?.syncStatus === 'failed' && (
          <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-900 text-xs rounded-full">
            Failed
          </span>
        )}
      </button>

      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSync}
        title="Sync Configuration"
        message="Force full configuration synchronization? This will rebuild and send the complete configuration to the edge device via MQTT."
        confirmText="Sync"
        variant="warning"
        isLoading={syncing}
      />
    </>
  );
};

export default SyncConfigButton;
